import React, { useId } from "react";
import type { TikTokCreatorInfo } from "../services/tiktokAuthentication";
import type { TikTokCreatorStatus } from "../hooks/useTikTokCreatorInfo";
import type { TikTokOptions } from "../types";
import {
  TIKTOK_BRANDED_CONTENT_POLICY_URL,
  TIKTOK_BRANDED_PRIVATE_MESSAGE,
  TIKTOK_DISCLOSURE_REQUIRED_MESSAGE,
  TIKTOK_MUSIC_USAGE_URL,
  TIKTOK_PRIVACY_LABELS,
  resolveTikTokOptions,
  tiktokLabelNotice,
  usesBrandedContentPolicy,
} from "../utils/tiktokOptions";

const FIELD_CLASS =
  "rounded-md border border-[var(--color-input-border)] bg-[var(--color-input-bg)] px-2 py-1 text-sm text-theme-fg shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-primary-light/40 disabled:cursor-not-allowed disabled:opacity-60";

interface TikTokPostSettingsProps {
  options?: TikTokOptions;
  creatorInfo: TikTokCreatorInfo | null;
  status: TikTokCreatorStatus;
  statusError?: string | null;
  videoUrl?: string;
  /** Messages from missingTikTokRequiredFields; shown so the user knows what is left. */
  problems?: string[];
  disabled?: boolean;
  onChange: (next: TikTokOptions) => void;
  /** Reports the real video length (seconds) once the browser has read its metadata. */
  onVideoDuration?: (seconds: number) => void;
  /** Unique per post so radio group names and ids do not collide across cards. */
  postId?: string;
}

/**
 * TikTok Direct Post settings (SGL-170). Follows TikTok's Content Sharing
 * Guidelines: creator shown, privacy from creator_info with no default,
 * interactions unchecked, commercial-content disclosure, AI label, duration check.
 * The consent declaration lives in TikTokConsentNotice, rendered above Publish.
 */
const TikTokPostSettings: React.FC<TikTokPostSettingsProps> = ({
  options,
  creatorInfo,
  status,
  statusError,
  videoUrl,
  problems = [],
  disabled = false,
  onChange,
  onVideoDuration,
  postId,
}) => {
  const fallbackId = useId();
  const uid = (postId || fallbackId).replace(/[^A-Za-z0-9_-]/g, "_");
  const modeName = `tiktok-mode-${uid}`;
  const privacyId = `tiktok-privacy-${uid}`;
  const opts = resolveTikTokOptions(options);
  const isInbox = opts.mode === "INBOX_DRAFT";
  const ready = status === "ready" && !!creatorInfo;
  const controlsDisabled = disabled || !ready;
  const update = (patch: Partial<TikTokOptions>) => {
    const next: TikTokOptions = { ...opts, ...patch };
    // An interaction TikTok says this creator can't allow is never sent as allowed.
    if (creatorInfo?.comment_disabled) next.allow_comment = false;
    if (creatorInfo?.duet_disabled) next.allow_duet = false;
    if (creatorInfo?.stitch_disabled) next.allow_stitch = false;
    onChange(next);
  };

  const privacyOptions = creatorInfo?.privacy_level_options ?? [];
  const privacyValue =
    opts.privacy_level && privacyOptions.includes(opts.privacy_level) ? opts.privacy_level : "";
  const labelNotice = tiktokLabelNotice(opts);
  const nickname = creatorInfo?.creator_nickname || creatorInfo?.creator_username;

  const setBrandContent = (checked: boolean) => {
    // Branded content can't be private: drop a SELF_ONLY choice so the user picks again.
    update({
      brand_content: checked,
      ...(checked && opts.privacy_level === "SELF_ONLY" ? { privacy_level: undefined } : {}),
    });
  };

  const setDisclosure = (on: boolean) =>
    update(
      on
        ? { commercial_content: true }
        : { commercial_content: false, brand_organic: false, brand_content: false }
    );

  const interactionBox = (
    key: "allow_comment" | "allow_duet" | "allow_stitch",
    label: string,
    creatorDisabled: boolean | undefined
  ) => {
    const off = controlsDisabled || !!creatorDisabled;
    return (
      <label
        key={key}
        className={`flex items-center gap-2 text-sm ${off ? "text-theme-fg-muted/60" : "text-theme-fg"}`}
        title={creatorDisabled ? `${label} is turned off in your TikTok settings` : undefined}
      >
        <input
          type="checkbox"
          className="h-4 w-4 rounded border-theme-border disabled:cursor-not-allowed"
          checked={!!opts[key] && !creatorDisabled}
          disabled={off}
          onChange={(e) => update({ [key]: e.target.checked } as Partial<TikTokOptions>)}
        />
        {label}
      </label>
    );
  };

  return (
    <div
      className="flex w-full flex-col gap-3 rounded-lg border border-theme-border bg-theme-background px-3 py-3 text-sm"
      data-testid="tiktok-post-settings"
    >
      <div className="text-theme-fg">
        {status === "loading" && <span>Loading your TikTok account…</span>}
        {ready && (
          <span>
            Posting to TikTok account: <strong>{nickname || "your TikTok account"}</strong>
          </span>
        )}
        {status === "reconnect" && (
          <span className="text-red-600">
            Your TikTok connection has expired.{" "}
            <a className="font-semibold underline" href="/social-posts?tab=connect">
              Reconnect TikTok
            </a>{" "}
            to publish.
          </span>
        )}
        {status === "not_connected" && (
          <span className="text-red-600">
            No TikTok account is connected.{" "}
            <a className="font-semibold underline" href="/social-posts?tab=connect">
              Reconnect TikTok
            </a>{" "}
            to publish.
          </span>
        )}
        {status === "error" && (
          <span className="text-red-600">
            TikTok isn&apos;t available right now, so this post can&apos;t be published. Please try
            again later.
            {statusError ? ` (${statusError})` : ""}
          </span>
        )}
      </div>

      <fieldset className="flex flex-wrap gap-4" disabled={controlsDisabled}>
        <legend className="sr-only">TikTok posting mode</legend>
        <label className="flex items-center gap-2 text-theme-fg">
          <input
            type="radio"
            name={modeName}
            checked={!isInbox}
            onChange={() => update({ mode: "DIRECT_POST" })}
          />
          Post to TikTok
        </label>
        <label className="flex items-center gap-2 text-theme-fg">
          <input
            type="radio"
            name={modeName}
            checked={isInbox}
            onChange={() => update({ mode: "INBOX_DRAFT" })}
          />
          Send to TikTok as a draft
        </label>
      </fieldset>

      {isInbox ? (
        <p className="text-xs text-theme-fg-muted">
          The video goes to your TikTok inbox. You&apos;ll choose privacy and other settings in the
          TikTok app.
        </p>
      ) : (
        <>
          <div className="flex items-center gap-2">
            <label htmlFor={privacyId} className="font-medium text-theme-fg-muted">
              Who can view this video: <span className="text-red-500">*</span>
            </label>
            <select
              id={privacyId}
              className={FIELD_CLASS}
              value={privacyValue}
              disabled={controlsDisabled}
              onChange={(e) => update({ privacy_level: e.target.value || undefined })}
            >
              <option value="">Select who can view this video</option>
              {privacyOptions.map((level) => {
                const blocked = level === "SELF_ONLY" && !!opts.brand_content;
                return (
                  <option
                    key={level}
                    value={level}
                    disabled={blocked}
                    title={blocked ? TIKTOK_BRANDED_PRIVATE_MESSAGE : undefined}
                  >
                    {TIKTOK_PRIVACY_LABELS[level] ?? level}
                    {blocked ? " (unavailable for branded content)" : ""}
                  </option>
                );
              })}
            </select>
          </div>
          {opts.brand_content && (
            <p className="text-xs text-theme-fg-muted">{TIKTOK_BRANDED_PRIVATE_MESSAGE}</p>
          )}

          <div className="flex flex-wrap items-center gap-4">
            <span className="font-medium text-theme-fg-muted">Allow users to:</span>
            {interactionBox("allow_comment", "Comment", creatorInfo?.comment_disabled)}
            {interactionBox("allow_duet", "Duet", creatorInfo?.duet_disabled)}
            {interactionBox("allow_stitch", "Stitch", creatorInfo?.stitch_disabled)}
          </div>

          <div className="flex flex-col gap-2">
            <label className="flex items-center gap-2 font-medium text-theme-fg">
              <input
                type="checkbox"
                role="switch"
                className="h-4 w-4 rounded border-theme-border"
                checked={!!opts.commercial_content}
                disabled={controlsDisabled}
                onChange={(e) => setDisclosure(e.target.checked)}
              />
              Disclose video content
            </label>
            <p className="text-xs text-theme-fg-muted">
              Turn on to disclose that this video promotes goods or services in exchange for
              something of value.
            </p>
            {opts.commercial_content && (
              <div className="ml-6 flex flex-col gap-1">
                <label className="flex items-center gap-2 text-theme-fg">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-theme-border"
                    checked={!!opts.brand_organic}
                    disabled={controlsDisabled}
                    onChange={(e) => update({ brand_organic: e.target.checked })}
                  />
                  Your brand
                </label>
                <label className="flex items-center gap-2 text-theme-fg">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-theme-border"
                    checked={!!opts.brand_content}
                    disabled={controlsDisabled}
                    onChange={(e) => setBrandContent(e.target.checked)}
                  />
                  Branded content
                </label>
                {!opts.brand_organic && !opts.brand_content && (
                  <p className="text-xs text-amber-700">{TIKTOK_DISCLOSURE_REQUIRED_MESSAGE}</p>
                )}
                {labelNotice && <p className="text-xs text-theme-fg-muted">{labelNotice}</p>}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <label className="flex items-center gap-2 font-medium text-theme-fg">
              <input
                type="checkbox"
                role="switch"
                className="h-4 w-4 rounded border-theme-border"
                checked={!!opts.is_aigc}
                disabled={controlsDisabled}
                onChange={(e) => update({ is_aigc: e.target.checked })}
              />
              AI-generated content
            </label>
            <p className="text-xs text-theme-fg-muted">
              Turn on if this video was created or significantly edited with AI. TikTok will label
              it as AI-generated.
            </p>
          </div>
        </>
      )}

      {videoUrl && (
        // Metadata probe only; the card already shows the video itself.
        <video
          data-testid="tiktok-duration-probe"
          src={videoUrl}
          preload="metadata"
          muted
          className="hidden"
          onLoadedMetadata={(e) => {
            const d = (e.currentTarget as HTMLVideoElement).duration;
            if (Number.isFinite(d) && onVideoDuration) onVideoDuration(d);
          }}
        />
      )}

      {problems.length > 0 && (
        <ul className="list-disc pl-5 text-xs text-amber-700" data-testid="tiktok-problems">
          {problems.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      )}
    </div>
  );
};

/** TikTok's consent declaration; must sit directly above the publish button. */
export const TikTokConsentNotice: React.FC<{ options?: TikTokOptions }> = ({ options }) => {
  const branded = usesBrandedContentPolicy(options);
  const linkClass = "font-semibold underline";
  return (
    <p className="text-xs text-theme-fg-muted" data-testid="tiktok-consent-notice">
      By posting, you agree to TikTok&apos;s{" "}
      {branded && (
        <>
          <a
            className={linkClass}
            href={TIKTOK_BRANDED_CONTENT_POLICY_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            Branded Content Policy
          </a>{" "}
          and{" "}
        </>
      )}
      <a
        className={linkClass}
        href={TIKTOK_MUSIC_USAGE_URL}
        target="_blank"
        rel="noopener noreferrer"
      >
        Music Usage Confirmation
      </a>
    </p>
  );
};

export default TikTokPostSettings;
